import { ChangeEvent, useState } from 'react';
import Breadcrumbs from './Breadcrumbs';
import { ContactDetails } from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import { submitContactUsForm } from '../service/submitContactUsForm';
import showToast from './ToastComponent';
import Loading from './Loading';
import { contactUsValidator } from '../validator/contactUsValidator';

const ContactUsForm = () => {
  const [details, setDetails] = useState<ContactDetails>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof ContactDetails,
  ) => {
    setDetails({
      ...details,
      [key]: e.target.value,
    } as ContactDetails);
  };

  const resetFields = () => {
    setDetails({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      message: '',
    });
  };

  const handleContactUsSubmit = async () => {
    try {
      if (!details) {
        setError(true);
        return;
      }

      const hasError = contactUsValidator(details);

      if (hasError) {
        setError(true);
        return;
      }
      setError(false);

      if (!user?.access_token) return;
      setLoading(true);
      const response = await submitContactUsForm(user?.access_token, details);
      if (response !== 200) {
        showToast({
          type: 'error',
          message: 'Error while submitting the form.',
        });
        return;
      }
      showToast({
        type: 'success',
        message: 'Form submitted successfully.',
      });
      resetFields();
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Error while submitting form.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="container-standard">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            href: '/home',
            children: <>Home</>,
          },
          {
            tag: 'p',
            children: <span>Contact Us</span>,
          },
        ]}
      />
      <div className="space-y-10 divide-y divide-gray-900/10 add-user">
        <div className="bg-white shadow rounded-xl">
          <div className="px-4 py-6 sm:p-8">
            <div className="grid  grid-cols-1 gap-x-6 gap-y-8">
              <div className="sm:col-span-2">
                <div className="sm:col-span-2 sm:mt-5">
                  <label
                    htmlFor="first-name"
                    className="text-sm font-semibold text-secondary-pink-700"
                  >
                    First name
                  </label>
                  <span className="ms-1 text-red-400">*</span>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="firstName"
                      id="first-name"
                      autoComplete="given-name"
                      className={`text-sm font-regular border rounded-md p-2 w-full ${
                        error && !details?.firstName
                          ? 'border-red-400'
                          : 'border-secondary-cornflowerblue-400'
                      }
                  `}
                      value={details?.firstName || ''}
                      onChange={(e) => handleTextChange(e, 'firstName')}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2 sm:mt-5">
                  <label
                    htmlFor="first-name"
                    className="text-sm font-semibold  text-secondary-pink-700"
                  >
                    Last name
                  </label>
                  <span className="ms-1 text-red-400">*</span>
                  <div className="mt-2">
                    <input
                      type="text"
                      value={details?.lastName || ''}
                      onChange={(e) => handleTextChange(e, 'lastName')}
                      autoComplete="last-name"
                      className={`text-sm font-regular border rounded-md p-2 w-full ${
                        error && !details?.lastName
                          ? 'border-red-400'
                          : 'border-secondary-cornflowerblue-400'
                      }
                  `}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2 sm:mt-5">
                  <label
                    htmlFor="email"
                    className="text-sm font-semibold  text-secondary-pink-700"
                  >
                    Email
                  </label>
                  <span className="ms-1 text-red-400">*</span>
                  <div className="mt-2">
                    <input
                      type="text"
                      value={details?.email || ''}
                      onChange={(e) => handleTextChange(e, 'email')}
                      autoComplete="email"
                      className={`text-sm font-regular border rounded-md p-2 w-full ${
                        error && !details?.email
                          ? 'border-red-400'
                          : 'border-secondary-cornflowerblue-400'
                      }
                  `}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2 sm:mt-5">
                  <label
                    htmlFor="mobile"
                    className="text-sm font-semibold  text-secondary-pink-700"
                  >
                    Mobile
                  </label>
                  <span className="ms-1 text-red-400">*</span>
                  <div className="mt-2">
                    <input
                      type="text"
                      value={details?.mobile || ''}
                      onChange={(e) => handleTextChange(e, 'mobile')}
                      autoComplete="mobile"
                      className={`text-sm font-regular border rounded-md p-2 w-full ${
                        error && !details?.mobile
                          ? 'border-red-400'
                          : 'border-secondary-cornflowerblue-400'
                      }
                  `}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2 sm:mt-5">
                  <label
                    htmlFor="message"
                    className="text-sm font-semibold  text-secondary-pink-700"
                  >
                    Message
                  </label>
                  <span className="ms-1 text-red-400">*</span>
                  <div className="mt-2">
                    <textarea
                      rows={5}
                      cols={50}
                      value={details?.message || ''}
                      onChange={(e) => handleTextChange(e, 'message')}
                      autoComplete="message"
                      className={`text-sm font-regular border rounded-md p-2 w-full ${
                        error && !details?.message
                          ? 'border-red-400'
                          : 'border-secondary-cornflowerblue-400'
                      }
                  `}
                    />
                  </div>
                  <div className="text-sm font-regular rounded-md p-2 w-full text-red-400">
                    * All fields are mandatory
                  </div>
                  <button
                    type="submit"
                    onClick={() => handleContactUsSubmit()}
                    className="bg-primary-blue-500 border border-solid border-primary-blue-500 
                             text-white px-2 py-3 w-24 mt-5 ml-auto rounded block"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsForm;
